import React, { useEffect, useRef } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { WCRexportreport, getLastWeldDate } from "../../Services/WI-services";
import { showSuccess, showInfo } from "../utility/ui/notifications/Toaster";
import { addWedlingValidUptoDate } from "../WPQTransaction/WPQObjects/WPQCommonUtils";
import { getWPQGridHeaderDataAllTypes } from "../../Services/wpq_groove-services";

/**
 * BACKEND API SERVICES REQUIRED FOR WCR FUNCTIONALITY:
 * 
 * 1. WI-services.js:
 *    - WCRexportreport(filterData): Fetches WI records for WCR report
 *      Router: GET /api/wi/wcr-export
 *      Query: welder filters, date ranges, plant location
 *      Returns: Array of WI records with WCR-compatible fields
 * 
 *    - getLastWeldDate(rowdata): Fetches most recent weld dates per welder
 *      Router: GET /api/wi/last-weld-date
 *      Query: welder ID, process filters
 *      Returns: Filtered records showing only latest activity
 * 
 * 2. wpq_groove-services.js:
 *    - getWPQGridHeaderDataAllTypes(data): Fetches WPQ header data
 *      Router: GET /api/wpq/grid-header-all-types
 *      Query: welder filters, status filters
 *      Returns: WPQ records with WQC_Pcat, WQC_Type, Process_Status
 * 
 * BACKEND ROUTER ENDPOINTS NEEDED:
 * 
 * WI Routes (/api/wi):
 * - GET /wcr-export: Export WI data for WCR report
 * - GET /last-weld-date: Get latest weld dates per welder-process
 * - PUT /update-status: Update WI_Status field for WCR filtering
 * - GET /by-plant: Filter WI records by plant location
 * 
 * WPQ Routes (/api/wpq):
 * - GET /grid-header-all-types: Get WPQ headers with process categories
 * - PUT /update-plant: Update WI_plant field in WPQ records
 * - GET /active-qualifications: Get active WPQ records for WCR
 * - PUT /process-status: Update Process_Status for qualification tracking
 * 
 * DATABASE SCHEMA UPDATES REQUIRED:
 * 
 * WPQ Table:
 * - WI_plant VARCHAR(50): Plant location for WCR compatibility
 * - WQC_Process TEXT: Pipe-separated process values
 * - WQC_Type TEXT: Pipe-separated type values  
 * - WQC_Pcat TEXT: Pipe-separated category values
 * - Process_Status JSON: Array of Active/Inactive per process
 * 
 * WI Table:
 * - WI_Status VARCHAR(20): Active/Inactive for WCR filtering
 * - WI_FileFormat VARCHAR(10): "WI" to distinguish from WPQ
 * - WI_ProcessCategory VARCHAR(100): Formatted as category#type
 * 
 * API RESPONSE FORMATS:
 * 
 * WCR Export Response:
 * {
 *   success: boolean,
 *   data: [{
 *     WI_Welder_Id: string,
 *     WI_Welder_Name: string,
 *     WI_SlipDate: date,
 *     WI_Process: string,
 *     WI_Type: string,
 *     WI_plant: string,
 *     WI_Slip: string,
 *     WI_Status: "Active"|"Inactive",
 *     WI_FileFormat: "WI"|"WPQ",
 *     WI_ProcessCategory: string
 *   }],
 *   totalRecords: number
 * }
 */

/**
 * WcrPdf Component - Generates Welder Continuity Record (WCR) PDF Report
 * 
 * Functionality:
 * - Fetches welder qualification data from WI and WPQ sources
 * - Processes and normalizes data to remove duplicates and filter active records
 * - Generates formatted PDF report with welder details, processes, and validity dates
 * - Groups records by welder ID and sorts by date/slip number
 * - Includes company header, pagination, and regulatory notes
 * - Automatically downloads the generated PDF file
 * 
 * IMPORTANT WCR FIELDS ADDED TO SOURCE PAGES:
 * 
 * WPQ General Page:
 * - WI_plant: Plant location field for WCR compatibility
 * - WQC_Process, WQC_Type, WQC_Pcat: Pipe-separated qualification data
 * - Process_Status: Active/Inactive status for each process type
 * 
 * WI Add/Edit Page:
 * - WI_Status: Active/Inactive toggle for WCR filtering
 * - WI_FileFormat: Set to "WI" to distinguish from WPQ records
 * - WI_ProcessCategory: Formatted as category#type for consistency
 * 
 * These fields ensure proper data flow for WCR report generation.
 */
const WcrPdf = (props) => {
  const pdfRef = useRef();

  /**
   * Processes WPQ (Welder Performance Qualification) data
   * 
   * FILTERING LOGIC:
   * - Parses pipe-separated values: WQC_Pcat|WQC_Type
   * - Groups statuses by category 
      * - Removes duplicates using Set with key: WelderId-Category-Type-Status
   * Grouping is done on Process Category if One WPQ header has same Process category and same type then it is shown once. If 2 WPQ of same welder has same process category and tyoe then it is shown 2wice
   * DATA TRANSFORMATION:
   * - Converts WPQ fields to WI format for unified processing:
   *   WPQ_WelderId → WI_Welder_Id
   *   WPQ_WelderName → WI_Welder_Name
   *   WPQ_Date → WI_SlipDate
   *   WPQ_RecordNo → WI_Slip
   * - Adds WI_FileFormat: "WPQ" to distinguish from WCR data
   * - Creates temp category string: "temp#type#category" for later extraction
   * 
   * It sorts WPQ and WI data and creates a log on when WPQ was created and when slip is generated
   * Sorting is based on the following criteria:
   * 1. WPQ_Date (descending)
   * 2. WPQ_RecordNo (descending)
   * 3. WPQ_WelderId (ascending)
   *
   * @param {Array} wpqData - Array of WPQ data objects
   * @returns {Array} Processed and normalized data for WCR report
   */
  const processWPQData = (wpqData) => {
    const seenPairs = new Set();
    const result = [];

    wpqData.forEach(item => {
      let pcatValues = item.WQC_Pcat?.split("|").map(p => p.trim()).filter(Boolean) || [];
      let typeValues = item.WQC_Type?.split("|").map(t => t.trim()).filter(Boolean) || [];

      if (item.wld_WPSType === "Tubesheet") {
        pcatValues = (item.WPSH_WPQ_Process + "|")?.split("|").map(p => p.trim()).filter(Boolean) || [];
        typeValues = (item.WPQD_WP_Type + "|")?.split("|").map(t => t.trim()).filter(Boolean) || [];
      }

      const maxLength = Math.min(pcatValues.length, typeValues.length);
      if (maxLength === 0) return;

      for (let i = 0; i < maxLength; i++) {
        const category = pcatValues[i];
        const type = typeValues[i];

        const comboKey = `${item.SK}-${item.WPQ_WelderId}-${category}-${type}`;
        if (!seenPairs.has(comboKey)) {
          seenPairs.add(comboKey);
          result.push({
            ...item,
            WI_Welder_Id: item.WPQ_WelderId,
            WI_Welder_Name: item.WPQ_WelderName,
            WI_SlipDate: item.WPQ_Date,
            WI_Process: item.WPSH_WPQ_Process?.replace(/,/g, "") || "",
            WI_Type: type,
            WI_plant: item.WPQ_PlantLocation,
            WI_Slip: item.WPQ_RecordNo,
            WI_ProcessCategory: `temp#${type}#${category}`,
            WI_FileFormat: "WPQ"
          });
        }
      }
    });

    return result;
  };
  /**
   * Main export function that orchestrates WCR report generation
   * 
   * LAST WELD DATE CHECKBOX LOGIC:
   * - When checkbox is checked: props.rowdata.WI_Last_Weld = true
   * - Calls getLastWeldDate() which queries WI_Continuity_tableName (separate DynamoDB table)
   * - Data source: TABLE_CONTINUITYRECORD with PK="WI_LASTWELD_DATE" and WI_FileFormat="Report"
   * - Returns filtered records showing only:
   *   • Most recent weld date for each welder-process combination
   *   • Eliminates historical records, keeping only current active qualifications
   *   • Reduces report size by showing latest activity per welder
   * 
   * FULL HISTORY MODE (checkbox unchecked):
   * - Fetches complete historical data from both sources:
   *   • WPQ data: All welder performance qualifications
   *   • WCR data: All welder continuity records
   * - Shows chronological progression of welder qualifications
   * 
   * DATA MERGING SEQUENCE:
   * - WCR data: Direct from WCRexportreport (already in WI format)
   * - WPQ data: Processed through processWPQData() to convert to WI format
   * - Final result: [...wcrData, ...processedWPQ] - WCR first, then WPQ
   * 
   * This ensures WCR records appear before WPQ records in the merged dataset
   */

  const exportWcr = async (data) => {
    try {
      console.log("Starting WCR export with data:", data);
      let result = [];

      if (props.rowdata?.WI_Last_Weld) {
        console.log("Processing Last Weld Date mode");
        result = await getLastWeldDate(props?.rowdata);
        console.log("Last Weld Date result:", result?.length, "records");
      } else {
        console.log("Processing Full History mode");
        const welderIds = data.WI_Welder_Id || [];
        console.log("Welder IDs to process:", welderIds);
        const CHUNK_SIZE = 20;

        if (welderIds.length <= CHUNK_SIZE) {
          console.log("Small dataset - processing normally");
          const [wpqData, wcrData] = await Promise.all([
            getWPQGridHeaderDataAllTypes(data),
            WCRexportreport(data)
          ]);
          console.log("WPQ Data received:", wpqData, "records");
          console.log("WCR Data received:", wcrData?.length, "records");
          const processedWPQ = processWPQData(wpqData);
          console.log("Processed WPQ Data:", processedWPQ, "records");
          result = [...wcrData, ...processedWPQ];
        } else {
          console.log("Large dataset - processing in chunks");
          result = await processWeldersInChunks(data, welderIds, CHUNK_SIZE);
        }
      }

      console.log("Final result for PDF generation:", result?.length, "records");
      console.log("Calling generatePdf with result:", result);
      generatePdf(result);
    } catch (error) {
      console.error("Error exporting WCR report:", error);
      console.error("Error stack:", error.stack);
      console.error("Error name:", error.name);
      console.error("Error message:", error.message);
      showInfo(`Failed to generate report: ${error.message || error}`);
    } finally {
      console.log("Setting loading to false");
      props.setLoading(false);
    }
  };

  const processWeldersInChunks = async (data, welderIds, chunkSize) => {
    let allResults = [];
    const totalChunks = Math.ceil(welderIds.length / chunkSize);

    console.log(`Processing ${welderIds.length} welders in ${totalChunks} chunks`);

    for (let i = 0; i < welderIds.length; i += chunkSize) {
      const chunk = welderIds.slice(i, i + chunkSize);
      const chunkNum = Math.floor(i / chunkSize) + 1;

      console.log(`Processing chunk ${chunkNum}/${totalChunks}: welders ${chunk.join(', ')}`);

      const chunkData = { ...data, WI_Welder_Id: chunk };

      try {
        const [wpqData, wcrData] = await Promise.all([
          getWPQGridHeaderDataAllTypes(chunkData),
          WCRexportreport(chunkData)
        ]);

        const processedWPQ = processWPQData(wpqData);
        allResults.push(...wcrData, ...processedWPQ);

        console.log(`Chunk ${chunkNum} complete: ${wcrData.length + processedWPQ.length} records`);

        // Small delay between chunks to prevent overwhelming backend
        if (i + chunkSize < welderIds.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Error processing chunk ${chunkNum}:`, error);
        showInfo(`Failed to process welders: ${chunk.join(', ')}`);
      }
    }

    console.log(`Total records processed: ${allResults.length}`);
    return allResults;
  };

  /**
   * Initiates PDF creation process
   * - Sets loading state for UI feedback
   * - Calls main export function with row data
   * - Hides the component dialog
   */
  const createPDF = () => {
    props.setLoading(true);
    const welderCount = props?.rowdata?.WI_Welder_Id?.length || 0;
    console.log(`Starting WCR generation for ${welderCount} welders`);
    exportWcr(props?.rowdata);
    props.onHide();
  };

  useEffect(() => {
    createPDF();
  }, []);

  /**
   * Core PDF generation function
   * 
   * GROUPING STRATEGY:
   * - Groups all records by WI_Welder_Id using groupBy() utility
   * - Each welder gets a separate page in the PDF
   * - Welder info (Sr No, Code, Name) appears only in first row per welder
   * 
   * SORTING HIERARCHY (within each welder group):
   * 1. WI_SlipDate: Newest dates first (descending)
   * 2. WI_FileFormat: WPQ records prioritized over WI records
   * 3. WI_Slip: Highest slip numbers first (numeric then string comparison)
   * 
   * This ensures most recent qualifications appear at top of each welder's section
   */
  const generatePdf = (Data) => {
    try {
      console.log("Starting PDF generation with data:", Data);
      console.log("Data length:", Data?.length);
      console.log("Data type:", typeof Data);
      console.log("First record sample:", Data?.[0]);

      if (!Data || Data.length === 0) {
        console.log("No data available for PDF generation");
        showInfo("There are no records matching to filters.");
        return;
      }

      const doc = new jsPDF();
      const groupedData = groupBy(Data, "WI_Welder_Id");

      Object.keys(groupedData).forEach((welderId, index) => {
        if (index > 0) doc.addPage();

        doc.setFontSize(10).setFont("bold");
        doc.text("WELDER CONTINUITY RECORD", 90, 20);
        doc.text("Godrej & Boyce Mfg. Co. Ltd", 15, 20);
        doc.text(`As on : ${new Date().toLocaleDateString("en-GB")}`, 167, 25);

        const header = [
          "Sr No", "Welder Code", "Welder Name", "Process / Type",
          "Plant / Slip No", "Weld Date", "Valid Upto"
        ];

        console.log(`Processing welder ${index + 1}: ${welderId}`);
        console.log(`Records for welder ${welderId}:`, groupedData[welderId]?.length);

        const uniqueWelderInfo = {
          [welderId]: {
            srNo: index + 1,
            id: groupedData[welderId][0].WI_Welder_Id,
            name: groupedData[welderId][0].WI_Welder_Name
          }
        };

        /**
         * DETAILED SORTING LOGIC for each welder's records:
         * 
         * PRIMARY: Date comparison (WI_SlipDate)
         * - Converts to Date objects and compares timestamps
         * - Returns dateB - dateA (newest first)
         * 
         * SECONDARY: File format priority (WI_FileFormat)
         * - WPQ format gets priority (1) over WI format (0)
         * - Ensures WPQ qualifications appear before WI records for same date
         * 
         * TERTIARY: Slip number ordering (WI_Slip)
         * - Attempts numeric parsing first (parseInt)
         * - Falls back to string comparison if non-numeric
         * - Higher numbers appear first (slipB - slipA)
         */
        groupedData[welderId].sort((a, b) => {
          const dateA = new Date(a.WI_SlipDate);
          const dateB = new Date(b.WI_SlipDate);
          if (dateA.getTime() !== dateB.getTime()) return dateB - dateA;

          const formatA = a.WI_FileFormat === "WPQ" ? 1 : 0;
          const formatB = b.WI_FileFormat === "WPQ" ? 1 : 0;
          if (formatA !== formatB) return formatA - formatB;

          const slipA = parseInt(a.WI_Slip, 10);
          const slipB = parseInt(b.WI_Slip, 10);
          if (!isNaN(slipA) && !isNaN(slipB)) return slipB - slipA;

          return String(b.WI_Slip).localeCompare(String(a.WI_Slip));
        });

        const tableData = groupedData[welderId].map((data, dataIndex) => {
          const slipDate = new Date(data.WI_SlipDate);
          const formattedSlipDate = slipDate.toLocaleDateString("en-GB");
          const validUptoDate = new Date(slipDate.getTime() + addWedlingValidUptoDate());
          const formattedValidUpto = validUptoDate.toLocaleDateString("en-GB");

          // Extract process category from WPQ temp format: "temp#type#category"
          // WCR data has direct category, WPQ data uses this encoded format
          const category = (data.WI_ProcessCategory || "").split("#")[2] || "";

          return [
            dataIndex === 0 ? uniqueWelderInfo[welderId].srNo : "",
            dataIndex === 0 ? uniqueWelderInfo[welderId].id : "",
            dataIndex === 0 ? uniqueWelderInfo[welderId].name : "",
            `${data.WI_Process === "GTAW" ? data.WI_Process : category} / ${data.WI_Type || ""}`,
            `${data.WI_plant} / ${data.WI_Slip}`,
            formattedSlipDate,
            formattedValidUpto
          ];
        });

        doc.autoTable({
          head: [header],
          body: tableData,
          theme: "plain",
          startY: 28,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: 10,
            cellPadding: { top: 0, right: 2, bottom: 0, left: 2 },
            lineWidth: 0.1,
            font: "bold",
            textColor: "#333333"
          },
          margin: { top: 20, bottom: 40 },
          didDrawPage: () => {
            const pageHeight = doc.internal.pageSize.height;
            doc.text("____________________________________________________________________________________________________", 15, pageHeight - 32);
            doc.text("Note : The Qualification of above welder will expire & will have to requalified if he has not welded with the above", 15, pageHeight - 28);
            doc.text("process during the period of next six months", 15, pageHeight - 23);
            doc.text("Maintained By : Welding Engineering Department", 15, pageHeight - 18);
          }
        });
        console.log(`AutoTable completed for welder ${welderId}`);
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 10);
      }

      console.log("PDF generation completed successfully");
      showSuccess("Successfully Exported.");
      doc.save("WCR_Report.pdf");
      console.log("PDF file saved");
    } catch (error) {
      console.error("Error generating PDF:", error);
      console.error("PDF Error stack:", error.stack);
      console.error("PDF Error name:", error.name);
      console.error("PDF Error message:", error.message);
      console.error("Data that caused error:", Data);
      showInfo(`PDF generation failed: ${error.message || error}`);
    }
  };

  /**
   * Utility function to group array objects by specified key
   */
  const groupBy = (arr, key) => {
    return arr.reduce((acc, obj) => {
      const groupKey = obj[key];
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(obj);
      return acc;
    }, {});
  };

  return <div ref={pdfRef}></div>;
};

export default WcrPdf;